import { DocumentType } from '~/api/graphql';
import { acordDocumentOptions } from './acord-document-options';

/**
 * A handful of ACORD forms are published as a separate document per state, and
 * each of those documents is its own DocumentType. Listed flat, they are 136 of
 * the ~220 rows in the form picker and nearly all of them are duplicates of
 * their neighbours — ACORD 137 alone contributes 48 rows.
 *
 * This module collapses each of those sets into a single row, and keeps the
 * mapping between that row plus a chosen state and the DocumentType the backend
 * actually generates. It is the frontend half of the state-variant families in
 * pkg/service/api/acordinfo; the backend derives the same relation from the
 * DocumentType enum, and `acord-form-families.test.ts` checks the two agree on
 * the set of variants by deriving this side from the same enum.
 */

/** Sentinel for "no state chosen — let Cara read it off the policy". */
export const STATE_FROM_POLICY = '';

/** Sentinel for "the form's own multi-state edition", where one exists. */
export const STATE_MULTI_STATE = 'MULTI_STATE';

export const US_STATE_NAMES: Record<string, string> = {
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming'
};

export type AcordFormFamily = {
  /** ACORD form number, e.g. '137'. */
  formNumber: string;
  /** Label for the single collapsed row. */
  title: string;
  /** Shown under the row and next to the state selector. */
  description: string;
  /** The DocumentType that names the form without naming a state. */
  placeholder: DocumentType;
  /** The multi-state edition, absent when the form is only published per state. */
  multiState?: DocumentType;
  /** State code to that state's DocumentType. */
  variants: Record<string, DocumentType>;
};

/**
 * The declared families. Adding a state variant to an existing family needs no
 * edit here — membership is derived from the DocumentType enum below — but a
 * whole new family does, which is deliberate: the backend needs a matching
 * ACORD_{N}_ANY_STATE enum value and prompt at the same time, and a silent
 * frontend-only collapse would hide half of the change.
 */
const FAMILY_DEFINITIONS: Array<
  Pick<AcordFormFamily, 'formNumber' | 'title' | 'description' | 'placeholder'>
> = [
  {
    formNumber: '50',
    title: 'ACORD 50 (Identification Card)',
    description: 'Published per state, plus a generic (state-agnostic) card',
    placeholder: DocumentType.Acord_50AnyState
  },
  {
    formNumber: '89',
    title: 'ACORD 89 (Residential Section)',
    description: 'Published per state, plus a generic (state-agnostic) section',
    placeholder: DocumentType.Acord_89AnyState
  },
  {
    formNumber: '90',
    title: 'ACORD 90 (Personal Auto Application)',
    description: 'Published only per state',
    placeholder: DocumentType.Acord_90AnyState
  },
  {
    formNumber: '137',
    title: 'ACORD 137 (Commercial Auto Coverage)',
    description: 'Published only per state',
    placeholder: DocumentType.Acord_137AnyState
  }
];

/**
 * Two-letter suffixes only. ACORD_50_NJ_SET and ACORD_50_PA_SET are the
 * multi-copy sheet editions of a state's card rather than the state's card, so
 * they must not be treated as New Jersey's or Pennsylvania's edition.
 */
const variantPattern = /^ACORD_(\d+)_([A-Z]{2})$/;
const multiStatePattern = /^ACORD_(\d+)$/;

const buildFamilies = (): AcordFormFamily[] =>
  FAMILY_DEFINITIONS.map((definition) => {
    const family: AcordFormFamily = { ...definition, variants: {} };

    for (const option of acordDocumentOptions) {
      const documentType = option.documentType as string;

      const variant = variantPattern.exec(documentType);
      if (variant && variant[1] === definition.formNumber && US_STATE_NAMES[variant[2]]) {
        family.variants[variant[2]] = option.documentType;
        continue;
      }

      const multiState = multiStatePattern.exec(documentType);
      if (multiState && multiState[1] === definition.formNumber) {
        family.multiState = option.documentType;
      }
    }

    return family;
  });

export const acordFormFamilies: AcordFormFamily[] = buildFamilies();

const familyByMember = new Map<DocumentType, AcordFormFamily>();
for (const family of acordFormFamilies) {
  familyByMember.set(family.placeholder, family);
  if (family.multiState) {
    familyByMember.set(family.multiState, family);
  }
  for (const variant of Object.values(family.variants)) {
    familyByMember.set(variant, family);
  }
}

/** The family a DocumentType belongs to, whether placeholder, multi-state, or variant. */
export const familyForDocumentType = (documentType: DocumentType): AcordFormFamily | undefined =>
  familyByMember.get(documentType);

/** State codes the family publishes an edition for, ordered by state name. */
export const familyStateCodes = (family: AcordFormFamily): string[] =>
  Object.keys(family.variants).sort((a, b) =>
    (US_STATE_NAMES[a] ?? a).localeCompare(US_STATE_NAMES[b] ?? b)
  );

/**
 * Everything the search box should match on a collapsed row. Someone who
 * remembers only "montana" should still find the row that can produce Montana's
 * form, even though the row itself never says Montana.
 */
export const familySearchKeywords = (family: AcordFormFamily): string =>
  [
    `ACORD ${family.formNumber}`,
    'state',
    ...familyStateCodes(family).flatMap((code) => [code, US_STATE_NAMES[code] ?? code])
  ].join(' ');

/**
 * A row of the collapsed picker: either a form with no state dimension, or a
 * family standing in for all of its state editions.
 */
export type AcordPickerRow =
  | { kind: 'form'; key: DocumentType; title: string }
  | { kind: 'family'; key: DocumentType; title: string; family: AcordFormFamily };

/** The state currently chosen for a selected family row. */
export type AcordFamilySelection = {
  family: AcordFormFamily;
  /** A state code, STATE_FROM_POLICY, or STATE_MULTI_STATE. */
  state: string;
  /** The DocumentType this selection currently resolves to. */
  documentType: DocumentType;
};

/**
 * Collapses a flat option list into picker rows, replacing each family's state
 * editions with the family's single row. Row order follows the input, with the
 * family row taking the position of its first member so ACORD 137 still sits
 * between 133 and 140.
 */
export const buildAcordPickerRows = (
  options: Array<{ documentType: DocumentType; title: string }>
): AcordPickerRow[] => {
  const rows: AcordPickerRow[] = [];
  const emitted = new Set<string>();

  for (const option of options) {
    const family = familyForDocumentType(option.documentType);
    if (!family) {
      rows.push({ kind: 'form', key: option.documentType, title: option.title });
      continue;
    }
    if (emitted.has(family.formNumber)) {
      continue;
    }
    emitted.add(family.formNumber);
    rows.push({ kind: 'family', key: family.placeholder, title: family.title, family });
  }

  return rows;
};

/**
 * Splits the selected document types into the keys the collapsed picker shows
 * and the state chosen for each selected family.
 *
 * The state is read back out of the document types rather than tracked
 * separately, so a value arriving from a saved workspace, an email, or a Zap
 * displays with the state it actually names.
 *
 * A family contributes one row, so a value that names two editions of the same
 * form — legal before the list was collapsed, and still legal on the wire —
 * shows the first and drops the rest. One request now asks for one state per
 * form; a second state is a second request.
 */
export const decodeAcordSelection = (
  documentTypes: DocumentType[]
): { keys: DocumentType[]; families: AcordFamilySelection[] } => {
  const keys: DocumentType[] = [];
  const families: AcordFamilySelection[] = [];
  const seenFamilies = new Set<string>();

  for (const documentType of documentTypes) {
    const family = familyForDocumentType(documentType);
    if (!family) {
      keys.push(documentType);
      continue;
    }
    if (seenFamilies.has(family.formNumber)) {
      continue;
    }
    seenFamilies.add(family.formNumber);

    let state = STATE_FROM_POLICY;
    if (documentType === family.multiState) {
      state = STATE_MULTI_STATE;
    } else if (documentType !== family.placeholder) {
      state =
        Object.entries(family.variants).find(([, variant]) => variant === documentType)?.[0] ??
        STATE_FROM_POLICY;
    }

    keys.push(family.placeholder);
    families.push({ family, state, documentType });
  }

  return { keys, families };
};

/** The DocumentType a family plus a chosen state resolves to. */
export const resolveFamilyDocumentType = (family: AcordFormFamily, state: string): DocumentType => {
  if (state === STATE_MULTI_STATE && family.multiState) {
    return family.multiState;
  }
  return family.variants[state] ?? family.placeholder;
};

/**
 * Turns picker keys back into document types, keeping the state already chosen
 * for any family that stays selected. A family that was just added has no state
 * yet and encodes to its placeholder, which is what tells the backend to read
 * the state off the policy.
 */
export const encodeAcordSelection = (
  keys: DocumentType[],
  previous: DocumentType[]
): DocumentType[] => {
  const previousStates = new Map<string, string>();
  for (const selection of decodeAcordSelection(previous).families) {
    previousStates.set(selection.family.formNumber, selection.state);
  }

  return keys.map((key) => {
    const family = familyForDocumentType(key);
    if (!family) {
      return key;
    }
    return resolveFamilyDocumentType(
      family,
      previousStates.get(family.formNumber) ?? STATE_FROM_POLICY
    );
  });
};

/** Replaces the state chosen for one selected family, leaving the rest alone. */
export const setFamilyState = (
  documentTypes: DocumentType[],
  family: AcordFormFamily,
  state: string
): DocumentType[] =>
  documentTypes.map((documentType) =>
    familyForDocumentType(documentType)?.formNumber === family.formNumber
      ? resolveFamilyDocumentType(family, state)
      : documentType
  );

/** Label for the state selector's current value, used in the row summary. */
export const familyStateLabel = (family: AcordFormFamily, state: string): string => {
  if (state === STATE_MULTI_STATE && family.multiState) {
    return 'All states';
  }
  return US_STATE_NAMES[state] ?? 'Determined from the policy';
};
